import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ErrorBoundary from '../components/Common/ErrorBoundary';
import ReplayCard from '../components/Live/ReplayCard';
import ReplayPlayerModal from '../components/Live/ReplayPlayerModal';
import { replayCategories } from '../data/replayData';

const replayStreams = [];

const {
  FiSearch, FiPlay, FiClock, FiEye, FiFilm,
  FiRefreshCw, FiCompass, FiX, FiStar, FiTrendingUp,
} = FiIcons;

// ── Featured hero replay ─────────────────────────────────────────────────────
const FeaturedReplay = ({ replay, onWatch }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    className="relative overflow-hidden rounded-[40px] shadow-2xl mb-12 cursor-pointer group"
    onClick={onWatch}
  >
    {/* Background */}
    <div className="absolute inset-0">
      <img
        src={replay.thumbnail}
        alt={replay.title}
        className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-br from-accent-navy/90 via-accent-navy/70 to-black/50" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(184,134,11,0.12)_0%,transparent_70%)]" />
    </div>

    {/* Content */}
    <div className="relative z-10 p-8 md:p-14 min-h-[320px] flex flex-col justify-end">
      <div className="flex items-center gap-3 mb-4">
        <span className="flex items-center gap-1.5 bg-accent-gold text-white px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest shadow-lg">
          <SafeIcon icon={FiStar} className="w-3 h-3" /> Featured Replay
        </span>
        <span className="px-3 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 text-white/70 rounded-full text-[9px] font-black uppercase tracking-widest">
          {replay.category}
        </span>
      </div>

      <h2 className="text-3xl md:text-5xl font-serif text-white leading-tight mb-4 max-w-2xl">
        {replay.title}
      </h2>
      <p className="text-white/60 text-sm md:text-base font-light leading-relaxed max-w-xl mb-6 line-clamp-2">
        {replay.description}
      </p>

      <div className="flex flex-wrap items-center gap-6 text-white/50 text-xs font-bold mb-8">
        <span className="flex items-center gap-1.5">
          <SafeIcon icon={FiClock} className="w-3.5 h-3.5 text-accent-gold" />
          {replay.duration}
        </span>
        <span className="flex items-center gap-1.5">
          <SafeIcon icon={FiEye} className="w-3.5 h-3.5 text-accent-gold" />
          {replay.views.toLocaleString()} views
        </span>
        <span className="text-white/40">{replay.organizer}</span>
      </div>

      <button className="flex items-center gap-3 px-8 py-4 bg-accent-gold text-accent-navy rounded-full text-xs font-black uppercase tracking-widest hover:bg-white transition-all shadow-xl shadow-accent-gold/20 w-fit">
        <SafeIcon icon={FiPlay} className="w-4 h-4" />
        Watch Featured Recording
      </button>
    </div>
  </motion.div>
);

// ── Empty state ──────────────────────────────────────────────────────────────
const EmptyState = ({ hasFilters, onReset, onExplore }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    className="col-span-full"
  >
    <div className="relative overflow-hidden rounded-[40px] bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900 shadow-2xl py-20 px-8 text-center">
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1419242902214-272b3f66ee7a?q=80&w=2113&auto=format&fit=crop"
          alt=""
          className="w-full h-full object-cover opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-800/80 to-slate-900/90" />
      </div>

      <div className="relative z-10">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', delay: 0.1 }}
          className="w-20 h-20 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center mx-auto mb-6 shadow-xl"
        >
          <SafeIcon icon={hasFilters ? FiSearch : FiFilm} className="w-8 h-8 text-white/70" />
        </motion.div>

        <h3 className="text-2xl sm:text-3xl font-serif text-white mb-3">
          {hasFilters ? 'No replays match your search' : 'No Recordings Available'}
        </h3>
        <p className="text-white/60 text-sm max-w-md mx-auto mb-10 leading-relaxed">
          {hasFilters
            ? "We couldn't find any recorded streams matching your criteria. Try broadening your search or clearing filters."
            : "Recorded memorial services will appear here after they are archived. Check back soon or explore live events."}
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          {hasFilters && (
            <button
              onClick={onReset}
              className="flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-slate-800 text-xs font-black uppercase tracking-widest hover:bg-white/90 transition-all shadow-xl"
            >
              <SafeIcon icon={FiRefreshCw} className="w-3.5 h-3.5" />
              Clear All Filters
            </button>
          )}
          <button
            onClick={onExplore}
            className="flex items-center gap-2 px-7 py-3.5 rounded-full bg-white/10 border border-white/20 text-white text-xs font-black uppercase tracking-widest hover:bg-white/20 transition-all backdrop-blur-sm"
          >
            <SafeIcon icon={FiCompass} className="w-3.5 h-3.5" />
            Explore Memorials
          </button>
        </div>
      </div>
    </div>
  </motion.div>
);

// ── Error fallback ───────────────────────────────────────────────────────────
const ReplayFallback = (error) => (
  <div className="col-span-full py-24 text-center bg-red-50 rounded-[40px] border-2 border-dashed border-red-200">
    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
      <span className="text-3xl">⚠️</span>
    </div>
    <h3 className="text-2xl font-serif text-red-700 mb-3">Failed to load replay library</h3>
    <p className="text-red-500 text-sm max-w-sm mx-auto mb-6">
      {error?.message || 'An unexpected rendering error occurred. Please refresh the page.'}
    </p>
    <button
      onClick={() => window.location.reload()}
      className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-red-600 text-white text-xs font-black uppercase tracking-widest hover:bg-red-700 transition-colors"
    >
      <SafeIcon icon={FiRefreshCw} className="w-3.5 h-3.5" />
      Refresh Page
    </button>
  </div>
);

// ── Main page ─────────────────────────────────────────────────────────────────
const ReplayLibrary = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedReplay, setSelectedReplay] = useState(null);

  // Featured replay = highest-viewed featured entry
  const featuredReplay = useMemo(
    () =>
      replayStreams
        .filter((r) => r.featured)
        .sort((a, b) => b.views - a.views)[0] || replayStreams[0],
    []
  );

  // Apply filters
  const filteredReplays = useMemo(() => {
    const lower = searchTerm.toLowerCase();
    return replayStreams.filter((replay) => {
      const matchSearch =
        !searchTerm ||
        replay.title.toLowerCase().includes(lower) ||
        replay.deceasedName?.toLowerCase().includes(lower) ||
        replay.familyName?.toLowerCase().includes(lower) ||
        replay.organizer.toLowerCase().includes(lower) ||
        replay.location?.toLowerCase().includes(lower);
      const matchCategory =
        activeCategory === 'All' || replay.category === activeCategory;
      return matchSearch && matchCategory;
    });
  }, [searchTerm, activeCategory]);

  const hasActiveSearch = searchTerm.trim() !== '' || activeCategory !== 'All';

  const handleReset = () => {
    setSearchTerm('');
    setActiveCategory('All');
  };

  const totalViews = replayStreams.reduce((sum, r) => sum + r.views, 0);

  return (
    <div className="pt-32 min-h-screen bg-brand-light pb-20">
      <div className="container mx-auto px-4 sm:px-6">
        {/* ── Hero ── */}
        <div className="max-w-4xl mx-auto text-center mb-10">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 bg-accent-navy/5 text-accent-navy px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest mb-6 border border-accent-navy/10"
          >
            <SafeIcon icon={FiFilm} className="w-3.5 h-3.5 text-accent-gold" />
            Stream Replay Archive
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl md:text-6xl font-serif text-accent-navy mb-6"
          >
            Replay Library
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-text-muted text-lg md:text-xl font-light leading-relaxed max-w-2xl mx-auto"
          >
            Watch recorded memorial services and tribute streams anytime. Search by name, family, or
            location to find a service you missed.
          </motion.p>
        </div>

        {/* ── Stats strip ── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-3xl mx-auto mb-12"
        >
          <div className="bg-white rounded-3xl p-5 shadow-sm border border-brand-dark flex items-center gap-4">
            <div className="w-11 h-11 bg-accent-gold/10 text-accent-gold rounded-2xl flex items-center justify-center">
              <SafeIcon icon={FiFilm} className="w-5 h-5" />
            </div>
            <div>
              <p className="text-2xl font-serif text-accent-navy leading-none">{replayStreams.length}</p>
              <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mt-1">Recordings</p>
            </div>
          </div>
          <div className="bg-white rounded-3xl p-5 shadow-sm border border-brand-dark flex items-center gap-4">
            <div className="w-11 h-11 bg-blue-50 text-blue-500 rounded-2xl flex items-center justify-center">
              <SafeIcon icon={FiEye} className="w-5 h-5" />
            </div>
            <div>
              <p className="text-2xl font-serif text-accent-navy leading-none">{(totalViews / 1000).toFixed(1)}k</p>
              <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mt-1">Total Views</p>
            </div>
          </div>
          <div className="col-span-2 md:col-span-1 bg-white rounded-3xl p-5 shadow-sm border border-brand-dark flex items-center gap-4">
            <div className="w-11 h-11 bg-emerald-50 text-emerald-500 rounded-2xl flex items-center justify-center">
              <SafeIcon icon={FiTrendingUp} className="w-5 h-5" />
            </div>
            <div>
              <p className="text-2xl font-serif text-accent-navy leading-none">{replayCategories.length - 1}</p>
              <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mt-1">Categories</p>
            </div>
          </div>
        </motion.div>

        {/* ── Featured replay ── */}
        {featuredReplay && !hasActiveSearch && (
          <FeaturedReplay replay={featuredReplay} onWatch={() => setSelectedReplay(featuredReplay)} />
        )}

        {/* ── Search bar ── */}
        <div className="max-w-4xl mx-auto mb-8">
          <div className="relative group">
            <div className="absolute inset-y-0 left-6 flex items-center pointer-events-none text-text-muted group-focus-within:text-accent-gold transition-colors">
              <SafeIcon icon={FiSearch} className="w-5 h-5" />
            </div>
            <input
              type="text"
              placeholder="Search by name, family, or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-16 pr-12 py-5 bg-white border-2 border-brand-dark rounded-full text-accent-navy placeholder:text-text-muted/50 focus:outline-none focus:border-accent-gold shadow-sm focus:shadow-xl transition-all font-medium text-lg"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute inset-y-2 right-2 flex items-center justify-center w-10 text-text-muted hover:text-accent-navy transition-colors"
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {/* ── Category filter chips ── */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-10">
          {replayCategories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-5 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-all border-2 ${
                activeCategory === cat
                  ? 'bg-accent-navy text-white border-accent-navy shadow-md'
                  : 'bg-white text-text-muted border-brand-dark hover:border-accent-gold/40 hover:text-accent-navy'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* ── Results count ── */}
        {hasActiveSearch && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center text-xs font-bold uppercase tracking-widest text-text-muted mb-8"
          >
            {filteredReplays.length === 0
              ? 'No results found'
              : `Showing ${filteredReplays.length} recording${filteredReplays.length !== 1 ? 's' : ''}`}
          </motion.p>
        )}

        {/* ── Grid ── */}
        <ErrorBoundary fallback={ReplayFallback}>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8">
            <AnimatePresence mode="popLayout">
              {filteredReplays.length > 0 ? (
                filteredReplays.map((replay, idx) => (
                  <ReplayCard
                    key={replay.id}
                    replay={replay}
                    idx={idx}
                    onClick={() => setSelectedReplay(replay)}
                  />
                ))
              ) : (
                <EmptyState
                  key="empty"
                  hasFilters={hasActiveSearch}
                  onReset={handleReset}
                  onExplore={() => navigate('/explore')}
                />
              )}
            </AnimatePresence>
          </div>
        </ErrorBoundary>
      </div>

      {/* ── Player modal ── */}
      <AnimatePresence>
        {selectedReplay && (
          <ReplayPlayerModal
            replay={selectedReplay}
            onClose={() => setSelectedReplay(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReplayLibrary;